/**
 * Route Detail Page JavaScript
 * Handles waypoint rendering to avoid template literal issues in templ
 */

import type { Map as MapboxMap, Marker, Popup } from 'mapbox-gl';
import type { Waypoint } from './types';
import { isDarkTheme, getWaypointTypeConfig } from './maps';
import { COLORS } from './colors';
import { escapeHtml, formatElevation } from './utils';

/**
 * Render waypoint list item HTML
 */
export function renderWaypointListItem(wp: Waypoint, idx: number): string {
    const typeConfig = getWaypointTypeConfig(wp.type);
    const name = wp.name || 'Waypoint ' + (idx + 1);

    let html = '<div class="flex items-start gap-3 p-3 bg-base-200/50 rounded-xl">';
    html += '<div class="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style="background-color: ' + typeConfig.color + '20;">';
    html += '<i class="ph-fill ' + typeConfig.icon + ' text-lg" style="color: ' + typeConfig.color + ';"></i>';
    html += '</div>';
    html += '<div class="flex-1 min-w-0">';
    html += '<div class="font-semibold text-base-content">' + escapeHtml(name) + '</div>';
    html += '<div class="text-sm text-base-content/60">' + typeConfig.label + '</div>';
    if (wp.description) {
        html += '<div class="text-sm text-base-content/70 mt-1">' + escapeHtml(wp.description) + '</div>';
    }
    // Coordinates
    html += '<div class="text-xs text-base-content/40 mt-1">';
    html += wp.lat.toFixed(5) + ', ' + wp.lng.toFixed(5);
    html += '</div>';
    html += '</div>';
    html += '</div>';

    return html;
}

/**
 * Create waypoint marker element for Mapbox
 */
export function createWaypointMarker(
    wp: Waypoint,
    idx: number,
    mapboxglLib: typeof mapboxgl,
    map: MapboxMap
): Marker {
    const typeConfig = getWaypointTypeConfig(wp.type);
    const name = wp.name || 'Waypoint ' + (idx + 1);

    // Create marker element
    const markerEl = document.createElement('div');
    markerEl.style.cssText =
        'width: 24px; height: 24px; ' +
        'background-color: ' + typeConfig.color + '; ' +
        'border: 2px solid white; ' +
        'border-radius: 50%; ' +
        'display: flex; ' +
        'align-items: center; ' +
        'justify-content: center; ' +
        'box-shadow: 0 2px 6px rgba(0,0,0,0.3); ' +
        'cursor: pointer;';

    const icon = document.createElement('i');
    icon.className = 'ph-bold ' + typeConfig.icon;
    icon.style.cssText = 'color: white; font-size: 11px;';
    markerEl.appendChild(icon);

    // Create popup HTML with theme-aware colors
    const dark = isDarkTheme();
    const popupColors = dark ? COLORS.popup.dark : COLORS.popup.light;
    const textColor = popupColors.text;
    const subtextColor = popupColors.subtext;

    let popupHtml = '<div style="color: ' + textColor + ';">';
    popupHtml += '<div class="font-semibold">' + escapeHtml(name) + '</div>';
    popupHtml += '<div class="text-sm" style="color: ' + subtextColor + ';">' + typeConfig.label + '</div>';
    if (wp.description) {
        popupHtml += '<div class="text-sm mt-1">' + escapeHtml(wp.description) + '</div>';
    }
    popupHtml += '</div>';

    const popup = new mapboxglLib.Popup({ offset: 20, closeButton: true, className: dark ? 'dark-popup' : '' })
        .setHTML(popupHtml);

    return new mapboxglLib.Marker({ element: markerEl })
        .setLngLat([wp.lng, wp.lat])
        .setPopup(popup)
        .addTo(map);
}

/**
 * Render waypoints list and markers
 */
export function renderWaypoints(
    waypoints: Waypoint[],
    map: MapboxMap | null,
    mapboxglLib: typeof mapboxgl | null
): void {
    if (!waypoints || waypoints.length === 0) return;

    // Show waypoints section
    const section = document.getElementById('waypoints-section');
    if (section) section.style.display = '';

    // Populate waypoints list
    const listEl = document.getElementById('waypoints-list');
    if (listEl) {
        listEl.innerHTML = waypoints.map((wp, idx) => renderWaypointListItem(wp, idx)).join('');
    }

    // Add waypoint markers to map
    if (map && mapboxglLib) {
        waypoints.forEach((wp, idx) => {
            createWaypointMarker(wp, idx, mapboxglLib, map);
        });
    }
}

/**
 * Render elevation stats HTML
 */
export function renderElevationStats(minEle: number, maxEle: number, useMetric = false): string {
    let html = '<span class="flex items-center gap-1">';
    html += '<i class="ph-bold ph-arrow-down text-emerald"></i>';
    html += 'Min: ' + formatElevation(minEle, useMetric);
    html += '</span>';
    html += '<span class="flex items-center gap-1">';
    html += '<i class="ph-bold ph-arrow-up text-accent"></i>';
    html += 'Max: ' + formatElevation(maxEle, useMetric);
    html += '</span>';
    return html;
}

